'use client';

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';

import styles from './native-booking-controls.module.css';

interface DateStripProps {
  selected: Date;
  onSelect: (date: Date) => void;
  daysToShow?: number;
  dates?: Date[];
  language?: string;
  /** Set of YYYY-MM-DD strings for dates that have available classes. When provided, dates not in this set are greyed out. */
  availableDates?: Set<string>;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getStartDate(): Date {
  const now = new Date();
  const start = new Date(now);
  // Before 5 AM, allow yesterday (for late-night slots)
  if (now.getHours() < 5) {
    start.setDate(start.getDate() - 1);
  }
  start.setHours(0, 0, 0, 0);
  return start;
}

export function DateStrip({
  selected,
  onSelect,
  daysToShow = 30,
  dates: suppliedDates,
  language = 'en',
  availableDates,
}: DateStripProps) {
  const { t } = useTranslation();
  const isNativeLayout = useNativeLayout();
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const [{ canScrollBack, canScrollForward, visibleDays }, setScrollState] =
    useState({ canScrollBack: false, canScrollForward: false, visibleDays: 5 });
  const startDate = getStartDate();

  const dates =
    suppliedDates ??
    Array.from({ length: daysToShow }, (_, i) => {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      return date;
    });
  const dateCount = suppliedDates?.length ?? daysToShow;

  const isRTL = language === 'ar';

  const updateScrollBoundary = useCallback(() => {
    const track = scrollRef.current;
    if (!track) return;
    const offset = isRTL ? -track.scrollLeft : track.scrollLeft;
    const next = {
      canScrollBack: offset > 1,
      canScrollForward: offset < track.scrollWidth - track.clientWidth - 1,
      visibleDays: Math.max(
        1,
        Math.min(dateCount, Math.floor((track.clientWidth + 8) / 58)),
      ),
    };
    setScrollState((current) =>
      current.canScrollBack === next.canScrollBack &&
      current.canScrollForward === next.canScrollForward &&
      current.visibleDays === next.visibleDays
        ? current
        : next,
    );
  }, [isRTL, dateCount]);

  // Keep navigation in sync with touch scrolling and viewport changes.
  useEffect(() => {
    const track = scrollRef.current;
    if (!track) return;
    const observer = new ResizeObserver(updateScrollBoundary);
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateScrollBoundary]);

  const dayFormatter = new Intl.DateTimeFormat(
    language === 'ar' ? 'ar' : 'en-US',
    {
      weekday: 'short',
    },
  );

  const monthFormatter = new Intl.DateTimeFormat(
    language === 'ar' ? 'ar' : 'en-US',
    {
      month: 'short',
    },
  );

  // Scroll selected date into view on mount and when selected date changes
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'instant',
      });
    }
  }, [selected]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.offsetWidth * 0.6;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div
      className="relative flex items-center gap-1"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <button
        type="button"
        onClick={() => scroll(isRTL ? 'right' : 'left')}
        className={cn(
          'text-muted-foreground hover:text-foreground shrink-0 p-1 transition-colors',
          styles.dateArrow,
          !canScrollBack && 'invisible',
        )}
        disabled={!canScrollBack}
        data-unavailable={!canScrollBack}
        aria-hidden={!canScrollBack}
        aria-label={t('bookings:prevDates')}
      >
        {isRTL ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>

      <div
        ref={scrollRef}
        style={
          isNativeLayout
            ? ({ '--date-visible-days': visibleDays } as CSSProperties)
            : undefined
        }
        onScroll={updateScrollBoundary}
        className={cn(
          'scrollbar-none flex gap-1.5 overflow-x-auto scroll-smooth',
          styles.dateTrack,
        )}
      >
        {dates.map((date) => {
          const isSelected = isSameDay(date, selected);
          const today = isToday(date);
          const dayName = dayFormatter.format(date);
          const dayNum = date.getDate();
          const monthName = monthFormatter.format(date);
          const isDisabled = availableDates
            ? !availableDates.has(formatDateKey(date))
            : false;

          return (
            <button
              key={date.toISOString()}
              type="button"
              aria-pressed={isSelected}
              aria-label={date.toLocaleDateString(language, {
                dateStyle: 'full',
              })}
              ref={isSelected ? selectedRef : undefined}
              onClick={() => !isDisabled && onSelect(date)}
              disabled={isDisabled}
              className={cn(
                'flex shrink-0 flex-col items-center rounded-xl px-3 py-2 transition-colors',
                'min-w-[3.5rem]',
                styles.dateOption,
                isDisabled
                  ? 'cursor-not-allowed opacity-30'
                  : isSelected
                    ? 'border-primary/35 bg-primary/10 text-foreground border shadow-none'
                    : 'hover:bg-muted border border-transparent',
                today && !isSelected && !isDisabled && 'border-primary/30',
              )}
            >
              <span className="text-[10px] leading-tight font-medium uppercase opacity-70">
                {dayName}
              </span>
              <span
                className={cn(
                  'text-lg leading-tight font-bold',
                  styles.dateNumber,
                )}
              >
                {dayNum}
              </span>
              <span className="text-[10px] leading-tight opacity-70">
                {monthName}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => scroll(isRTL ? 'left' : 'right')}
        className={cn(
          'text-muted-foreground hover:text-foreground shrink-0 p-1 transition-colors',
          styles.dateArrow,
          !canScrollForward && 'invisible',
        )}
        disabled={!canScrollForward}
        aria-hidden={!canScrollForward}
        aria-label={t('bookings:nextDates')}
      >
        {isRTL ? (
          <ChevronLeft className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}
